import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import useBinanceMarketData from '../hooks/useBinanceMarketData';
import { useAccount } from './AccountContext';
import { useAccountType } from './AccountTypeContext';
import { positionEventEmitter } from '../services/positionEventEmitter';

export interface OpenPosition {
  id: string;
  symbol: string;
  type: 'Buy' | 'Sell';
  size: string;
  entry: string;
  current: string;
  pnl: string;
  pnlPercent: string;
  leverage: number;
  timestamp: number;
  isAI?: boolean; // Flag to indicate if position was created by AI
}

export interface PendingOrder {
  id: string;
  symbol: string;
  type: 'Buy' | 'Sell';
  size: string;
  price: string;
  stopPrice?: string;
  orderType: 'limit' | 'stop';
  status: 'Pending' | 'Filled' | 'Cancelled';
  leverage: number;
  timestamp: number;
}

export interface ClosedPosition {
  id: string;
  symbol: string;
  type: 'Buy' | 'Sell';
  size: string;
  entry: string;
  close: string;
  pnl: string;
  pnlPercent: string;
  closeTime: string;
  status: 'Success' | 'Cancelled';
  timestamp: number;
  isAI?: boolean; // Flag to indicate if position was created by AI
}

interface PositionContextType {
  openPositions: OpenPosition[];
  pendingOrders: PendingOrder[];
  closedPositions: ClosedPosition[];
  totalUnrealizedPnL: number;
  addOpenPosition: (position: Omit<OpenPosition, 'id' | 'timestamp'>) => OpenPosition;
  addPendingOrder: (order: Omit<PendingOrder, 'id' | 'timestamp'>) => void;
  closePosition: (positionId: string, closePrice: string) => void;
  cancelOrder: (orderId: string) => void;
  editOrder: (orderId: string, newPrice: string, newSize: string) => void;
  fillOrder: (orderId: string, fillPrice: string) => void;
  updateCurrentPrices: (priceUpdates: { [symbol: string]: string }) => void;
}

const PositionContext = createContext<PositionContextType | undefined>(undefined);

export const usePositionContext = () => {
  const context = useContext(PositionContext);
  if (context === undefined) {
    throw new Error('usePositionContext must be used within a PositionProvider');
  }
  return context;
};

interface PositionProviderProps {
  children: ReactNode;
}

export const PositionProvider: React.FC<PositionProviderProps> = ({ children }) => {
  const [openPositions, setOpenPositions] = useState<OpenPosition[]>([]);
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
  const [closedPositions, setClosedPositions] = useState<ClosedPosition[]>([]);
  
  // Get live market data for real-time price updates
  const { getMarketDataForSymbol } = useBinanceMarketData();
  
  // Get account context for profit updates
  const { addProfit } = useAccount();
  
  // Get account type context
  const { accountType } = useAccountType();

  // Ref to track previous values to prevent unnecessary emissions
  const prevValuesRef = useRef<{ totalUsedMargin: number; totalUnrealizedPnL: number; accountType: string } | null>(null);

  const generateId = () => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  };

  // Calculate total used margin from all open positions
  const calculateTotalUsedMargin = useCallback((positions: OpenPosition[]): number => {
    return positions.reduce((total, position) => {
      const entryPrice = parseFloat(position.entry);
      const size = parseFloat(position.size);
      const leverage = position.leverage;
      const marginUsed = (entryPrice * size) / leverage;
      return total + marginUsed;
    }, 0);
  }, []);

  // Calculate margin from pending orders
  const calculatePendingOrdersMargin = useCallback((orders: PendingOrder[]): number => {
    return orders.reduce((total, order) => {
      const orderPrice = parseFloat(order.orderType === 'stop' && order.stopPrice ? order.stopPrice : order.price);
      const orderSize = parseFloat(order.size);
      const leverage = order.leverage;
      const marginUsed = (orderPrice * orderSize) / leverage;
      return total + marginUsed;
    }, 0);
  }, []);

  // Calculate total unrealized P&L from all open positions
  const calculateTotalUnrealizedPnL = useCallback((positions: OpenPosition[]): number => {
    return positions.reduce((total, position) => {
      const entryPrice = parseFloat(position.entry);
      const currentPrice = parseFloat(position.current);
      const size = parseFloat(position.size);
      
      let pnl: number;
      if (position.type === 'Buy') {
        pnl = (currentPrice - entryPrice) * size;
      } else {
        pnl = (entryPrice - currentPrice) * size;
      }
      
      return total + pnl;
    }, 0);
  }, []);

  // Function to calculate P&L for a position
  const calculatePnL = useCallback((entryPrice: number, currentPrice: number, size: number, type: 'Buy' | 'Sell') => {
    let pnl: number;
    if (type === 'Buy') {
      pnl = (currentPrice - entryPrice) * size;
    } else {
      pnl = (entryPrice - currentPrice) * size;
    }
    const pnlPercent = ((pnl / (entryPrice * size)) * 100).toFixed(2);
    const pnlFormatted = pnl >= 0 ? `+$${pnl.toFixed(2)}` : `-$${Math.abs(pnl).toFixed(2)}`;
    const pnlPercentFormatted = pnl >= 0 ? `+${pnlPercent}%` : `-${Math.abs(parseFloat(pnlPercent))}%`;
    
    return { pnl: pnlFormatted, pnlPercent: pnlPercentFormatted };
  }, []);

  const addOpenPosition = (positionData: Omit<OpenPosition, 'id' | 'timestamp'>) => {
    // Calculate initial P&L
    const entryPrice = parseFloat(positionData.entry);
    const currentPrice = parseFloat(positionData.current);
    const size = parseFloat(positionData.size);
    const { pnl, pnlPercent } = calculatePnL(entryPrice, currentPrice, size, positionData.type);
    
    const newPosition: OpenPosition = {
      ...positionData,
      id: generateId(),
      timestamp: Date.now(),
      pnl,
      pnlPercent,
    };
    
    // Log trade execution
    if (positionData.isAI) {
      console.log('🤖 AI Trade executed:', {
        symbol: newPosition.symbol,
        type: newPosition.type,
        size: newPosition.size,
        entry: newPosition.entry,
        leverage: newPosition.leverage
      });
    } else {
      console.log('👤 Manual Trade executed:', {
        symbol: newPosition.symbol,
        type: newPosition.type,
        size: newPosition.size,
        entry: newPosition.entry,
        leverage: newPosition.leverage
      });
    }
    
    setOpenPositions(prev => {
      const updatedPositions = [newPosition, ...prev];
      
      // Emit position update after state change
      setTimeout(() => {
        const totalUsedMargin = calculateTotalUsedMargin(updatedPositions);
        const totalUnrealizedPnL = calculateTotalUnrealizedPnL(updatedPositions);
        positionEventEmitter.emit('positionsUpdated', { 
          totalUsedMargin, 
          totalUnrealizedPnL, 
          accountType 
        });
      }, 0);
      
      return updatedPositions;
    });
    
    return newPosition; // Return the position so AI context can track it
  };

  const addPendingOrder = (orderData: Omit<PendingOrder, 'id' | 'timestamp'>) => {
    const newOrder: PendingOrder = {
      ...orderData,
      id: generateId(),
      timestamp: Date.now(),
    };
    setPendingOrders(prev => [newOrder, ...prev]);
  };

  const closePosition = (positionId: string, closePrice: string) => {
    const position = openPositions.find(p => p.id === positionId);
    if (!position) return;

    // Calculate P&L using the helper function
    const entryPrice = parseFloat(position.entry);
    const closePriceNum = parseFloat(closePrice);
    const size = parseFloat(position.size);
    const { pnl, pnlPercent } = calculatePnL(entryPrice, closePriceNum, size, position.type);

    // Calculate numeric P&L for profit addition
    let numericPnL: number;
    if (position.type === 'Buy') {
      numericPnL = (closePriceNum - entryPrice) * size;
    } else {
      numericPnL = (entryPrice - closePriceNum) * size;
    }

    // Calculate margin that was used for this position (to return it to balance)
    const leverage = position.leverage;
    const marginUsed = (entryPrice * size) / leverage;

    // Add profit to account (separate P&L and margin return)
    const description = `${position.symbol} ${position.type} position closed at ${closePrice}`;
    addProfit(numericPnL, marginUsed, description, accountType);

    // Add to closed positions
    const closedPosition: ClosedPosition = {
      id: position.id,
      symbol: position.symbol,
      type: position.type,
      size: position.size,
      entry: position.entry,
      close: closePrice,
      pnl,
      pnlPercent,
      closeTime: new Date().toLocaleString(),
      status: 'Success',
      timestamp: Date.now(),
      isAI: position.isAI, // Preserve AI flag
    };

    setClosedPositions(prev => [closedPosition, ...prev]);
    setOpenPositions(prev => {
      const updatedPositions = prev.filter(p => p.id !== positionId);
      
      // Emit position update after state change
      setTimeout(() => {
        const totalUsedMargin = calculateTotalUsedMargin(updatedPositions);
        const totalUnrealizedPnL = calculateTotalUnrealizedPnL(updatedPositions);
        positionEventEmitter.emit('positionsUpdated', { 
          totalUsedMargin, 
          totalUnrealizedPnL, 
          accountType 
        });
      }, 0);
      
      return updatedPositions;
    });
  };

  const cancelOrder = (orderId: string) => {
    const order = pendingOrders.find(o => o.id === orderId);
    if (!order) return;

    // Calculate margin that was reserved for this order (to return it to balance)
    const marginUsed = (parseFloat(order.price) * parseFloat(order.size)) / order.leverage;

    // Return the margin to the account balance (no P&L, just margin return)
    if (addProfit) {
      addProfit(0, marginUsed, `Margin returned for cancelled ${order.symbol} order`, accountType);
    }

    // Add to closed positions as cancelled
    const cancelledOrder: ClosedPosition = {
      id: order.id,
      symbol: order.symbol,
      type: order.type,
      size: order.size,
      entry: order.price,
      close: order.price,
      pnl: '$0.00',
      pnlPercent: '0.00%',
      closeTime: new Date().toLocaleString(),
      status: 'Cancelled',
      timestamp: Date.now(),
    };

    setClosedPositions(prev => [cancelledOrder, ...prev]);
    setPendingOrders(prev => prev.filter(o => o.id !== orderId));
  };

  const editOrder = (orderId: string, newPrice: string, newSize: string) => {
    setPendingOrders(prev => 
      prev.map(order => 
        order.id === orderId 
          ? { ...order, price: newPrice, size: newSize }
          : order
      )
    );
  };

  const fillOrder = useCallback((orderId: string, fillPrice: string) => {
    const order = pendingOrders.find(o => o.id === orderId);
    if (!order) return;

    // Calculate initial P&L (should be $0.00 since entry = current)
    const entryPrice = parseFloat(fillPrice);
    const currentPrice = parseFloat(fillPrice);
    const size = parseFloat(order.size);
    const { pnl, pnlPercent } = calculatePnL(entryPrice, currentPrice, size, order.type);

    // Move to open positions
    const newPosition: OpenPosition = {
      id: order.id,
      symbol: order.symbol,
      type: order.type,
      size: order.size,
      entry: fillPrice,
      current: fillPrice,
      pnl,
      pnlPercent,
      leverage: order.leverage, // Use the leverage from the original order
      timestamp: Date.now(),
    };

    setOpenPositions(prev => [newPosition, ...prev]);
    setPendingOrders(prev => prev.filter(o => o.id !== orderId));
  }, [pendingOrders, calculatePnL]);

  const updateCurrentPrices = useCallback((priceUpdates: { [symbol: string]: string }) => {
    setOpenPositions(prev => {
      const updatedPositions = prev.map(position => {
        const newPrice = priceUpdates[position.symbol];
        if (!newPrice) return position;

        // Only update if the price actually changed
        if (position.current === newPrice) {
          return position;
        }

        const entryPrice = parseFloat(position.entry);
        const currentPrice = parseFloat(newPrice);
        const size = parseFloat(position.size);
        const { pnl, pnlPercent } = calculatePnL(entryPrice, currentPrice, size, position.type);

        return {
          ...position,
          current: newPrice,
          pnl,
          pnlPercent,
        };
      });
      
      // Only return new array if something actually changed
      const hasChanges = updatedPositions.some((pos, index) => 
        pos.current !== prev[index].current || 
        pos.pnl !== prev[index].pnl || 
        pos.pnlPercent !== prev[index].pnlPercent
      );
      
      return hasChanges ? updatedPositions : prev;
    });
  }, [calculatePnL]);

  // Emit position updates after state changes to avoid React warnings
  useEffect(() => {
    const positionsMargin = calculateTotalUsedMargin(openPositions);
    const pendingOrdersMargin = calculatePendingOrdersMargin(pendingOrders);
    const totalUsedMargin = positionsMargin + pendingOrdersMargin;
    const totalUnrealizedPnL = calculateTotalUnrealizedPnL(openPositions);
    
    // Check if values have actually changed to prevent infinite loops
    const currentValues = { totalUsedMargin, totalUnrealizedPnL, accountType };
    const prevValues = prevValuesRef.current;
    
    if (!prevValues || 
        prevValues.totalUsedMargin !== totalUsedMargin || 
        prevValues.totalUnrealizedPnL !== totalUnrealizedPnL || 
        prevValues.accountType !== accountType) {
      
      // Update the ref with current values
      prevValuesRef.current = currentValues;
      
      // Emit updates for both positions and pending orders
      positionEventEmitter.emit('positionsUpdated', currentValues);
    }
  }, [openPositions, pendingOrders, accountType, calculateTotalUsedMargin, calculatePendingOrdersMargin, calculateTotalUnrealizedPnL]);

  // Automatically update current prices and P&L when positions change
  useEffect(() => {
    if (openPositions.length === 0 && pendingOrders.length === 0) return;

    // Create price updates object from market data
    const priceUpdates: { [symbol: string]: string } = {};
    
    // Update open positions
    openPositions.forEach(position => {
      const marketData = getMarketDataForSymbol(position.symbol);
      if (marketData && marketData.price !== position.current) {
        priceUpdates[position.symbol] = marketData.price;
      }
    });

    // Check pending orders for execution
    pendingOrders.forEach(order => {
      const marketData = getMarketDataForSymbol(order.symbol);
      if (marketData) {
        const currentPrice = parseFloat(marketData.price);
        const orderPrice = parseFloat(order.price);
        const stopPrice = order.stopPrice ? parseFloat(order.stopPrice) : null;
        
        let shouldExecute = false;
        
        if (order.orderType === 'limit') {
          // Limit order logic
          if (order.type === 'Buy') {
            // Buy limit: execute when current price <= limit price
            shouldExecute = currentPrice <= orderPrice;
          } else {
            // Sell limit: execute when current price >= limit price
            shouldExecute = currentPrice >= orderPrice;
          }
        } else if (order.orderType === 'stop') {
          // Stop order logic
          if (order.type === 'Buy') {
            // Buy stop: execute when current price >= stop price
            shouldExecute = stopPrice ? currentPrice >= stopPrice : false;
          } else {
            // Sell stop: execute when current price <= stop price
            shouldExecute = stopPrice ? currentPrice <= stopPrice : false;
          }
        }
        
        if (shouldExecute) {
          fillOrder(order.id, marketData.price);
        }
      }
    });

    // Only update if we have price data that's different from current prices
    if (Object.keys(priceUpdates).length > 0) {
      updateCurrentPrices(priceUpdates);
    }
  }, [openPositions, pendingOrders, getMarketDataForSymbol]);

  // Calculate total unrealized P&L
  const totalUnrealizedPnL = calculateTotalUnrealizedPnL(openPositions);

  const value: PositionContextType = {
    openPositions,
    pendingOrders,
    closedPositions,
    totalUnrealizedPnL,
    addOpenPosition,
    addPendingOrder,
    closePosition,
    cancelOrder,
    editOrder,
    fillOrder,
    updateCurrentPrices,
  };

  return (
    <PositionContext.Provider value={value}>
      {children}
    </PositionContext.Provider>
  );
};
